import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, type FormikHelpers } from 'formik';
import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type Dispatch,
  type HTMLAttributes,
  type Ref,
  type SetStateAction
} from 'react';
import { Button, Card, Col, Form, Nav, Row, Spinner } from 'react-bootstrap';
import { object, string } from 'yup';
import _ from 'lodash';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import {
  bestPageFoundColumnConfig,
  competitorsColumnConfig
} from '@/app/KeywordResearch/config/competitorsColumnConfig';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import gapApi, {
  useLazyGetCompetitorsQuery,
  useGetKeywordListFuncMutation,
  useLazyGetKeywordListQuery,
  useLazyGetBestPageFoundQuery,
  useExportTableCompetitorsMutation,
  useLazyGetGapChartQuery,
  useLazyGetCompetitorDomainMetricsQuery
} from '@/app/KeywordResearch/api/gapApi';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import { useDispatch } from 'react-redux';
import Flex from '@/components/common/Flex';
import Select from 'react-select';
import { useAppContext } from '@/Main';
import CompetitorsBarChart from '../components/keyword-competitor/CompetitorsBarChart';
import Lottie from 'lottie-react';
import LottieLoading from '@/assets/LottieLoading.json';
import CompetitorsDate from '../components/keyword-competitor/CompetitorsDate';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { keywordResearchTooltips } from '@/app/KeywordResearch/tooltip/keywordResearchTooltips';
import { ToolAccessDenied } from '@/app/common/components/ToolAccessGuard';
import useToolAccess from '@/app/common/hooks/useToolAccess';
import OptionLayoutPlace from '@/app/common/components/OptionsLayout/OptionLayoutPlace';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import useDomain from '@/hooks/useDomain';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import classNames from 'classnames';
import { intentMap, purposeMap } from '../config/keywordGapColumnConfig';
import GapCustomDifficultyFilters from '../components/keyword-gap/GapCustomDifficultyFilters';
import GapCustomSearchVolumeFilters from '../components/keyword-gap/GapCustomSearchVolumeFilters';
import FiltersComponentSkeleton from '@/app/common/components/filters/FiltersComponentSkeleton';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import CompetitorsCustomPosFilters from '../components/keyword-competitor/CompetitorsCustomPosFilters';
import { removeFilters } from '@/app/common/reducers/filterSlice';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import { useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';
import type { GapCompetitorDomainMetricsData } from '@/app/KeywordResearch/types/gapApiTypes';
import CompetitorDomainMetricsPanel from '../components/keyword-competitor/CompetitorDomainMetricsPanel';
import {
  CompetitorsChartPreload,
  CompetitorDomainMetricsPreload
} from '../components/keyword-competitor/CompetitorsPreloads';

type BreadcrumbItem = { text: string; href: string };
type OrganizerOption = {
  value: string;
  label: string;
  desc: string;
  ex: string;
};
type PlaceOption = {
  value: string;
  label: string;
  flag: string;
  isDisabled?: boolean;
};
type CompetitorsFormValues = { start_url: string };
type OptionLayoutProps = {
  data: Pick<OrganizerOption, 'label' | 'desc' | 'ex'>;
  innerProps: HTMLAttributes<HTMLDivElement>;
  innerRef: Ref<HTMLDivElement>;
  isSelected: boolean;
};
type AgGridWrapperProps = Record<string, unknown>;
type AgGridSkeletonProps = Record<string, unknown>;
type FiltersComponentProps = Record<string, unknown>;
type ChartProps = Record<string, unknown>;

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;
const TypedAgGridSkeleton = AgGridSkeleton as ComponentType<AgGridSkeletonProps>;
const TypedFiltersComponent =
  FiltersComponent as unknown as ComponentType<FiltersComponentProps>;
const TypedCompetitorsDate =
  CompetitorsDate as unknown as ComponentType<ChartProps>;
const TypedCompetitorsBarChart =
  CompetitorsBarChart as unknown as ComponentType<ChartProps>;

const organizerOptions: OrganizerOption[] = [
  {
    value: 'root',
    label: 'کل دامنه',
    desc: ' دامنه و شامل همه زیر دامنه‌ها ',
    ex: '*.example.com/*'
  },
  {
    value: 'domain',
    label: 'دامنه',
    desc: 'فقط دامنه مشخص شده',
    ex: 'example.com/*'
  },
  {
    value: 'path',
    label: 'مسیر',
    desc: 'مسیر مشخص شده و پوشه‌ها',
    ex: 'example.com/blog/*'
  },
  {
    value: 'exact',
    label: 'نشانی دقیق',
    desc: 'فقط نشانی درج شده',
    ex: 'example.com/product/s23'
  }
];

const organizerOptionsPlace: PlaceOption[] = [
  {
    value: 'ir',
    label: 'ایران',
    flag: 'https://static.mizfa.tools/flags/ir.webp'
  },
  {
    value: 'global',
    label: 'جهانی',
    flag: 'https://static.mizfa.tools/flags/global.webp',
    isDisabled: true
  }
];

const Competitors = () => {
  const isLgUp = useMediaQuery(breakpointsMediaQuery.lg);
  const [exportTable] = useExportTableCompetitorsMutation();

  const domainRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useDispatch();
  const gridRef = useRef<HTMLDivElement>(null);
  const [, setBreadcrumbData] = useOutletContext<
    [BreadcrumbItem[], Dispatch<SetStateAction<BreadcrumbItem[]>>]
  >();
  const [isNoData, setIsNoData] = useState(true);
  const [selectedMode, setSelectedMode] = useState(organizerOptions[0]);
  const [selectedPlace, setSelectedPlace] = useState(organizerOptionsPlace[0]);
  const { domain_slug, domain } = useDomain();
  const {
    config: { isDark }
  } = useAppContext();
  const [, setSearchParams] = useSearchParams();
  const [getChartData, { data: chartData, isFetching: isChartLoading }] =
    useLazyGetGapChartQuery();
  const [getKeywordList, { data: kwData, isFetching }] =
    useLazyGetKeywordListQuery();
  const [
    getBestPageFound,
    { data: bestPageData, isFetching: isFetchingBestPage }
  ] = useLazyGetBestPageFoundQuery();
  const [getKeywordsListFunc] = useGetKeywordListFuncMutation();
  const [
    getCompetitors,
    { data: competitorsData, isFetching: isCompetitorsLoading }
  ] = useLazyGetCompetitorsQuery();
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;
  const [rank_range, setRank_range] = useState('1-30');
  const access = useToolAccess('competitors_keywords');
  const [
    getCompetitorDomainMetrics,
    { data: domainMetricsResponse, isFetching: isDomainMetricsLoading }
  ] = useLazyGetCompetitorDomainMetricsQuery();

  useEffect(() => {
    setBreadcrumbData([{ text: 'کلمات رقیب', href: '#' }]);
  }, []);

  useEffect(() => {
    access.haveAccess &&
      (async () => {
        await getCompetitors({
          location: selectedPlace.value,
          host: encodeURIComponent(domain)
        });
      })();
  }, [access.haveAccess]);

  useEffect(() => {
    dispatch(gapApi.util.invalidateTags(['get_competitors']));
  }, [domain]);

  const onSubmit = async (
    values: CompetitorsFormValues,
    _actions: FormikHelpers<CompetitorsFormValues>
  ) => {
    setIsNoData(false);
    setSearchParams({});
    dispatch(removeFilters());
    const body = {
      location: selectedPlace.value,
      model: selectedMode.value,
      host: encodeURIComponent(values.start_url),
      page: 1,
      perPage: 50
    };

    getCompetitors({
      location: selectedPlace.value,
      host: encodeURIComponent(values.start_url)
    });
    getKeywordList(body).then(() => {
      selectedMode.value === 'exact'
        ? window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth'
          })
        : '';
      getBestPageFound({
        host: encodeURIComponent(values.start_url),
        location: selectedPlace.value,
        model: selectedMode.value,
        rank_range
      });
    });
    getChartData({
      location: selectedPlace.value,
      model: selectedMode.value,
      host: encodeURIComponent(values.start_url)
    });
    getCompetitorDomainMetrics({
      host: encodeURIComponent(values.start_url)
    });
  };
  if (access.isLoading) return <></>;
  if (!access.haveAccess) {
    return (
      <ToolAccessDenied access={access} tool_label="تب کلمات رقیب" />
    );
  }

  if (!access.isLoading)
    return (
      <Formik<CompetitorsFormValues>
        onSubmit={onSubmit}
        initialValues={{
          start_url: ''
        }}
        // اعتبار سنجی پیوند وارد شده
        validationSchema={object().shape({
          start_url: string()
            .required('وارد کردن دامنه رقیب الزامی می‌باشد')
            .url('دامنه معتبر نیست (باید با http یا https شروع بشه).')
        })}
      >
        {({
          values,
          errors,
          handleChange,
          touched,
          handleBlur,
          handleSubmit,
          setFieldValue,
          submitForm
        }) => {
          const competitors =
            (
              competitorsData?.data as
                | { competitors?: Array<{ site: string }> }
                | undefined
            )?.competitors ?? [];
          const chart = chartData?.data as
            | { linear_echart?: unknown; bar_echart?: unknown }
            | undefined;
          const domainMetrics = domainMetricsResponse?.data as
            | GapCompetitorDomainMetricsData
            | undefined;
          const keywordData = kwData?.data as
            | { total?: number; rows?: unknown[] }
            | undefined;
          useEffect(() => {
            if (!isNoData)
              getBestPageFound({
                host: encodeURIComponent(values.start_url),
                rank_range,
                model: selectedMode.value,
                location: selectedPlace.value
              });
          }, [rank_range]);
          return (
            <>
              <Form onSubmit={handleSubmit}>
                <Row className={'justify-content-md-center'}>
                  <Col md={12}>
                    <div
                      style={{ maxWidth: 800 }}
                      className="w-100 my-3 mx-auto"
                    >
                      <style>{`
            .customized-option:hover{
              background-color:black!important;
            }
            .badge-subtle-secondary {
              height: 37px;
              font-size: 13px;
              border: var(--falcon-border-width) solid var(--falcon-gray-300);
            }    

            #bestResult .ag-theme-custom {
            --ag-borders:0px !important;
            --ag-row-border-width:0px !important;
            box-shadow:unset !important;
            }
           `}</style>
                      <div
                        className={classNames({
                          'input-group input-group-md': isLgUp,
                          'd-flex flex-column gap-2': !isLgUp
                        })}
                      >
                        <div style={{ width: isLgUp ? '160px' : '100%' }}>
                          <Select
                            menuShouldScrollIntoView={true}
                            closeMenuOnSelect={true}
                            isSearchable={false}
                            onChange={(e: OrganizerOption | null) => {
                              if (e) setSelectedMode(e);
                            }}
                            options={organizerOptions}
                            classNamePrefix="react-select"
                            value={selectedMode}
                            // className="rounded-end-0"
                            maxMenuHeight={300}
                            styles={{
                              control: (baseStyles, _state) => ({
                                ...baseStyles,
                                paddingLeft: '0px',
                                paddingRight: '10px',
                                color: 'var(--falcon-emphasise) !important',
                                boxShadow: 'none !important',
                                cursor: 'pointer',
                                borderRadius: isLgUp ? '0px 5px 5px 0px' : '5px'
                              }),
                              input: (baseStyles, _state) => ({
                                ...baseStyles,
                                color: 'var(--falcon-emphasise) !important',
                                boxShadow: 'none !important'
                              })
                            }}
                            components={{
                              Option: OptionLayout
                            }}
                          />
                        </div>
                        <div style={{ width: isLgUp ? '150px' : '100%' }}>
                          <Select
                            menuShouldScrollIntoView={true}
                            closeMenuOnSelect={true}
                            isSearchable={false}
                            onChange={(e: PlaceOption | null) => {
                              if (e && !e.isDisabled) setSelectedPlace(e);
                            }}
                            isOptionDisabled={option =>
                              Boolean(option.isDisabled)
                            }
                            options={organizerOptionsPlace}
                            classNamePrefix="react-select"
                            value={selectedPlace}
                            // className="rounded-end-0"
                            maxMenuHeight={300}
                            styles={{
                              control: (baseStyles, _state) => ({
                                ...baseStyles,
                                paddingLeft: '0px',
                                paddingRight: '10px',
                                color: 'var(--falcon-emphasise) !important',
                                boxShadow: 'none !important',
                                cursor: 'pointer',
                                borderRadius: isLgUp ? '0px' : '5px'
                              }),
                              input: (_baseStyles, _state) => ({
                                color: 'var(--falcon-emphasise) !important',
                                boxShadow: 'none !important',
                                display: 'none !important'
                              })
                            }}
                            components={{
                              Option: OptionLayoutPlace,
                              SingleValue: SingleValueFlag
                            }}
                          />
                        </div>
                        <div className="d-flex flex-1">
                          <Form.Control
                            type="text"
                            isInvalid={Boolean(
                              touched.start_url && errors.start_url
                            )}
                            isValid={Boolean(
                              touched.start_url && !errors.start_url
                            )}
                            ref={domainRef}
                            style={{
                              direction: domainRef?.current?.value?.length
                                ? 'ltr'
                                : 'rtl',
                              borderRadius: isLgUp ? 0 : '0px 5px 5px 0px'
                            }}
                            placeholder={`نشانی شامل https یا http باشه`}
                            value={values.start_url}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="start_url"
                          />
                          <ChildrensTooltip
                            placement="top"
                            className="p-0"
                            html={
                              isAccessAllowed()
                                ? ''
                                : 'شما دسترسی لازم را ندارید'
                            }
                          >
                            <Button
                              variant="falcon-primary"
                              title="جستجو"
                              disabled={
                                !isAccessAllowed() ||
                                isFetching ||
                                isChartLoading
                              }
                              className="d-flex align-items-center h-100"
                              type="submit"
                              style={{ borderRadius: '5px 0 0 5px' }}
                            >
                              {isFetching || isChartLoading ? (
                                <Spinner
                                  style={{
                                    width: '16px',
                                    height: '16px'
                                  }}
                                />
                              ) : (
                                <FontAwesomeIcon icon="search" />
                              )}
                            </Button>
                          </ChildrensTooltip>
                        </div>

                        <div
                          style={{
                            position: isLgUp ? 'absolute' : undefined,
                            bottom: '-30px'
                          }}
                        >
                          {errors.start_url ? (
                            <Form.Label className="text-danger">
                              {errors.start_url}
                            </Form.Label>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Form>
              <Row>
                <Col className="my-3">
                  {!isCompetitorsLoading ? (
                    <>
                      {competitors.length > 0 && (
                        <Card>
                          <FalconCardHeader
                            titleTag="h6"
                            className="py-2"
                            light
                            title={
                              <div>
                                رقیب‌های پیشنهادی{' '}
                                <HelpTooltip
                                  html={
                                    keywordResearchTooltips.competitors
                                      .suggested_competitors
                                  }
                                />
                              </div>
                            }
                          />
                          <Card.Body>
                            <Flex
                              className="gap-2"
                              // direction={isLgUp ? 'row' : 'column'}
                              wrap="wrap"
                            >
                              {competitors.map(item => (
                                <div
                                  key={item.site}
                                  onClick={() => {
                                    setFieldValue(
                                      'start_url',
                                      'https://' + item.site
                                    ).then(() => {
                                      submitForm();
                                    });
                                  }}
                                >
                                  <Button
                                    variant={isDark ? 'dark' : 'falcon-default'}
                                    style={{ fontSize: '12px' }}
                                    className="p-0 badge m-0 cursor-pointer d-flex align-items-center"
                                  >
                                    <span className="p-2">
                                      <span>{item.site} </span>
                                    </span>
                                    <span className="vr opacity-25" />
                                    <span className="h-100 p-2 d-flex">
                                      <FontAwesomeIcon
                                        icon="search"
                                        className="mx-1"
                                        style={{
                                          color: 'inherit'
                                        }}
                                      />
                                    </span>
                                  </Button>
                                </div>
                              ))}
                            </Flex>
                          </Card.Body>
                        </Card>
                      )}
                    </>
                  ) : (
                    <Card>
                      <FalconCardHeader
                        titleTag="h6"
                        className="py-2"
                        light
                        title={
                          <div>
                            رقیب‌های پیشنهادی{' '}
                            <HelpTooltip
                              html={
                                keywordResearchTooltips.competitors
                                  .suggested_competitors
                              }
                            />
                          </div>
                        }
                      />
                      <Card.Body>
                        <Flex
                          className="gap-2"
                          // direction={isLgUp ? 'row' : 'column'}
                          wrap="wrap"
                        >
                          {Array.from({ length: 10 }).map((_item, index) => (
                            <TextLoading
                              key={index}
                              isWidthPx
                              width={_.random(90, 160)}
                              height={30}
                            />
                          ))}
                        </Flex>
                      </Card.Body>
                    </Card>
                  )}
                </Col>
              </Row>
              {!isNoData ? (
                <Row className="g-3">
                  <Col lg={6} md={12}>
                    {!isChartLoading ? (
                      <>
                        {chart && (
                          <TypedCompetitorsDate
                            data={chart.linear_echart}
                            title="تغییر کلمات بالا در دو بازه زمانی"
                            tooltip={
                              keywordResearchTooltips.competitors.changesCard
                            }
                          />
                        )}
                      </>
                    ) : (
                      <CompetitorsChartPreload />
                    )}
                  </Col>
                  <Col lg={6} md={12}>
                    {!isChartLoading ? (
                      <>
                        {chart && (
                          <TypedCompetitorsBarChart
                            style={{ height: '200px' }}
                            data={chart.bar_echart}
                            title="تغییر کلمات بالا در کل بازه"
                            tooltip={
                              keywordResearchTooltips.competitors.bar_echart
                            }
                          />
                        )}
                      </>
                    ) : (
                      <CompetitorsChartPreload />
                    )}
                  </Col>
                  {isDomainMetricsLoading ? (
                    <CompetitorDomainMetricsPreload />
                  ) : domainMetrics ? (
                    <CompetitorDomainMetricsPanel data={domainMetrics} />
                  ) : null}
                  <Col xs={12}>
                    <Card id="bestResult" className="mb-0">
                      <Card.Header
                        style={{ zIndex: 1 }}
                        as={Flex}
                        justifyContent="between"
                        alignItems="center"
                        className="ps-0 py-0 position-relative border-bottom"
                      >
                        <Nav
                          as="ul"
                          className="nav-tabs border-0 flex-wrap chart-tab tab-active-caret"
                        >
                          <div className="m-auto fs--1 mx-3">
                            بازه رتبه TOP:
                          </div>
                          {['1-3', '1-10', '1-30', '1-50', '1-100'].map(v => (
                            <Nav.Item key={v} className="z-3" as="li">
                              <Nav.Link
                                onClick={() => setRank_range(v)}
                                className=" mb-0 text-nowrap d-flex gap-1 align-items-center navLinkForceColor py-3 cursor-pointer"
                                active={v === rank_range}
                              >
                                <span
                                  className={
                                    v === rank_range ? 'fw-bold' : 'fw-normal'
                                  }
                                >
                                  {v}
                                </span>
                              </Nav.Link>
                            </Nav.Item>
                          ))}
                        </Nav>
                      </Card.Header>
                      <Card.Body className="p-0">
                        {!isFetchingBestPage && !isFetching ? (
                          <TypedAgGridWrapper
                            hideFooter
                            settings={{ pagination: false }}
                            height="456px"
                            columns={bestPageFoundColumnConfig}
                            data={bestPageData?.data ?? []}
                          />
                        ) : (
                          <div>
                            <TypedAgGridSkeleton height="406px" />
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12} id="kwData">
                    <>
                      {!isCompetitorsLoading ? (
                        <>
                          <TypedFiltersComponent
                            selectItems={{
                              purpose: Object.entries(purposeMap).map(
                                ([k, v]) => ({
                                  label: v,
                                  value: k
                                })
                              ),
                              intent: Object.entries(intentMap).map(([k]) => ({
                                label: k,
                                value: k
                              }))
                            }}
                            columns={competitorsColumnConfig}
                            SampleFilters={
                              <>
                                <CompetitorsCustomPosFilters />
                                <GapCustomDifficultyFilters />
                                <GapCustomSearchVolumeFilters />
                              </>
                            }
                          />
                        </>
                      ) : (
                        <FiltersComponentSkeleton />
                      )}
                      {!isFetching ? (
                        <div ref={gridRef} className="mt-3">
                          <TypedAgGridWrapper
                            settings={{
                              pagination: true,
                              fullscreen: false,
                              changeHeight: false,
                              resetGrid: false,
                              saveGrid: false,
                              sideBar: false,
                              export: {
                                enabled: true,
                                fetchFunction: exportTable,
                                additionalData: {
                                  location: selectedPlace.value,
                                  model: selectedMode.value,
                                  host: encodeURIComponent(values.start_url)
                                }
                              }
                            }}
                            additionalData={{
                              location: selectedPlace.value,
                              model: selectedMode.value,
                              host: encodeURIComponent(values.start_url)
                            }}
                            height="746px"
                            fetchFunction={getKeywordsListFunc}
                            columns={competitorsColumnConfig}
                            total={keywordData?.total}
                            data={keywordData?.rows}
                          />
                        </div>
                      ) : (
                        <TypedAgGridSkeleton height="696px" />
                      )}
                    </>
                  </Col>
                </Row>
              ) : (
                <div className="justify-content-center align-items-center">
                  <div
                    className="mb-3 text-center"
                    style={{ width: '250px', margin: 'auto' }}
                  >
                    <Lottie animationData={LottieLoading} />
                  </div>
                  <div className="mt-6 fw-medium text-center">
                    برای مشاهده کلمات رقیب، دامنه رو در بخش بالا وارد کن و دکمه
                    ذره بین رو بزن.{' '}
                  </div>
                </div>
              )}
            </>
          );
        }}
      </Formik>
    );
};

export default Competitors;

const OptionLayout = (props: OptionLayoutProps) => {
  const { innerProps, innerRef } = props;
  return (
    <>
      <style>{`
      .customized-searchbox-option:hover{
        background-color:var(--falcon-gray-link-hover-300) !important;
      }
      .customized-searchbox-option{
        background-color:var(--falcon-tertiary-bg) !important;
      }

      `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          backgroundColor: props.isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className="p-2 cursor-pointer border border-1  customized-searchbox-option"
      >
        <div className="fs--1 text-900">{props.data.label} </div>
        <div className="text-secondary fs--2">
          {props.data.desc}
          <br />
          <div style={{ direction: 'ltr', textAlign: 'right' }}>
            {props.data.ex}
          </div>
        </div>
      </div>
    </>
  );
};
